import { useState, useRef, useCallback, useEffect, useContext } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { AuthContext } from "@/context/AuthContext";
import {
  Zap, Search, Globe, Smartphone, Monitor, Sparkles,
  AlertTriangle, CheckCircle2, Info, Download, RefreshCw,
  Clock, Activity, FileImage, TrendingUp, X,
  ChevronRight, Image, BarChart2, Code2, Type, Rss,
  Rocket, ArrowUpRight, Target, Wrench, Package,
} from "lucide-react";
import { JasrMark } from "@/components/JasrLogo";
import { usePlatform } from "@/context/PlatformContext";
import { BulkImageOptimizer, ScriptPanel, ManualAction } from "@/components/OptimizationEngine";
import OptimizationLoop from "@/components/OptimizationLoop";
import SEOEngine from "@/components/SEOEngine";

/* ── helpers ── */
const fmtBytes = (n) => {
  if (n < 1024) return n + " B";
  if (n < 1024 * 1024) return Math.round(n / 1024) + " KB";
  return (n / 1024 / 1024).toFixed(1) + " MB";
};
function scoreColor(s) {
  if (s >= 90) return "#10B981";
  if (s >= 50) return "#F59E0B";
  return "#EF4444";
}
function scoreBg(s) {
  if (s >= 90) return "rgba(16,185,129,0.1)";
  if (s >= 50) return "rgba(245,158,11,0.1)";
  return "rgba(239,68,68,0.1)";
}
function scoreLabel(s) {
  if (s >= 90) return "ممتاز";
  if (s >= 50) return "يحتاج تحسين";
  return "ضعيف";
}
const CAT_META = {
  images:  { label: "الصور",     color: "#004EEB", icon: Image },
  scripts: { label: "السكربتات", color: "#F59E0B", icon: Code2 },
  fonts:   { label: "الخطوط",   color: "#8B5CF6", icon: Type },
  content: { label: "المحتوى",  color: "#10B981", icon: Rss },
  seo:     { label: "SEO",       color: "#EC4899", icon: TrendingUp },
  mobile:  { label: "الجوال",   color: "#0EA5E9", icon: Smartphone },
};
const IMPACT_META = {
  high:   { label: "تأثير عالٍ",  color: "#EF4444", bg: "#FEF2F2", border: "#FECACA" },
  medium: { label: "تأثير متوسط", color: "#F59E0B", bg: "#FFFBEB", border: "#FDE68A" },
  low:    { label: "تأثير منخفض", color: "#94A3B8", bg: "#F8FAFC", border: "#E2E8F0" },
};

/* ── Score Gauge ── */
function ScoreGauge({ score, label, size = 120 }) {
  const r = size / 2 - 10;
  const c = 2 * Math.PI * r;
  const col = scoreColor(score);
  const offset = c - (score / 100) * c;
  return (
    <div className="flex flex-col items-center gap-2">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#F1F5F9" strokeWidth={8} />
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={col} strokeWidth={8}
          strokeDasharray={c} strokeDashoffset={offset} strokeLinecap="round"
          transform={`rotate(-90 ${size/2} ${size/2})`}
          style={{ transition: "stroke-dashoffset 1.4s cubic-bezier(.4,0,.2,1)" }} />
        <text x={size/2} y={size/2 - 4} textAnchor="middle" fill={col} fontSize={size > 100 ? 26 : 20} fontWeight="800" fontFamily="monospace">{score}</text>
        <text x={size/2} y={size/2 + 12} textAnchor="middle" fill="#94A3B8" fontSize={9}>/100</text>
      </svg>
      <div className="text-center">
        <div className="text-[11px] font-bold text-[var(--text-secondary)]">{label}</div>
        <div className="text-[9px] font-bold mt-0.5 px-2 py-0.5 rounded-full"
          style={{ background: scoreBg(score), color: scoreColor(score) }}>
          {scoreLabel(score)}
        </div>
      </div>
    </div>
  );
}

/* ── Vital Card ── */
function VitalCard({ label, data }) {
  if (!data) return null;
  const col = data.score == null ? "#94A3B8" : scoreColor(Math.round(data.score * 100));
  return (
    <div className="bg-[var(--bg-card)] rounded-xl border border-[var(--border-color)] p-3 shadow-sm hover:shadow-md transition-all">
      <div className="text-[9px] font-bold uppercase tracking-wider text-[var(--text-muted)] mb-1.5">{label}</div>
      <div className="text-base font-bold font-mono" style={{ color: col }}>{data.value}</div>
    </div>
  );
}

/* ── Recommendation Card ── */
function RecommendationCard({ rec, idx }) {
  const cat = CAT_META[rec.category] || CAT_META.content;
  const imp = IMPACT_META[rec.impact] || IMPACT_META.medium;
  const CatIcon = cat.icon;
  return (
    <div className="bg-[var(--bg-card)] rounded-xl border p-4 hover:shadow-md transition-all group"
      style={{ borderColor: "#E2E8F0" }}
      data-testid={`rec-card-${idx}`}>
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
          style={{ background: imp.bg }}>
          <CatIcon className="w-4 h-4" style={{ color: cat.color }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start gap-2 mb-1.5">
            <span className="text-xs font-bold text-[var(--text-primary)] flex-1 leading-snug">{rec.title}</span>
            <span className="text-[9px] font-bold px-2 py-0.5 rounded-full border shrink-0"
              style={{ background: imp.bg, color: imp.color, borderColor: imp.border }}>{imp.label}</span>
          </div>
          <p className="text-[11px] text-[var(--text-secondary)] leading-relaxed mb-2">{rec.description}</p>
          <div className="flex items-center gap-1.5 text-[10px] font-semibold" style={{ color: cat.color }}>
            <ChevronRight className="w-3 h-3 shrink-0" />
            <span>{rec.action}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Overall Health Card ─────────────────────────────────────────────── */
function OverallHealthCard({ overallScore, grade, url, viewMode, onToggle, scoreDiff }) {
  const col   = scoreColor(overallScore);
  const bg    = scoreBg(overallScore);
  const gradeColors = { A: "#10B981", B: "#3B82F6", C: "#F59E0B", D: "#F97316", F: "#EF4444", "—": "#94A3B8" };
  return (
    <div className="rounded-2xl overflow-hidden shadow-sm border border-[var(--border-color)] bg-[var(--bg-card)]"
      data-testid="overall-health-card">
      <div className="flex flex-wrap items-center gap-5 px-6 py-5">
        {/* Grade */}
        <div className="flex flex-col items-center justify-center w-20 h-20 rounded-2xl shrink-0"
          style={{ background: bg, border: `1.5px solid ${col}22` }}>
          <span className="text-4xl font-black leading-none" style={{ color: gradeColors[grade] ?? col }}>{grade}</span>
          <span className="text-[9px] font-bold text-[var(--text-muted)] mt-0.5 uppercase tracking-wider">Score</span>
        </div>
        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xl font-black" style={{ color: col }}>{overallScore}/100</span>
            {scoreDiff != null && Math.abs(scoreDiff) > 0 && (
              <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${scoreDiff > 0 ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-500"}`}>
                {scoreDiff > 0 ? "↑" : "↓"} {Math.abs(scoreDiff)} مقارنة بالسابق
              </span>
            )}
          </div>
          <div className="text-xs font-bold text-[var(--text-secondary)] mt-0.5 truncate">{url}</div>
          <div className="text-[11px] text-[var(--text-muted)] mt-1">
            {overallScore >= 90 ? "متجرك في المقدمة — أداء استثنائي" :
             overallScore >= 75 ? "أداء جيد — بعض الفرص القابلة للتحسين" :
             overallScore >= 60 ? "متوسط — يوجد مجال تحسين كبير" :
             overallScore >= 45 ? "دون المتوسط — تحتاج تدخلاً عاجلاً" :
             "أداء ضعيف — اتبع التوصيات أدناه فوراً"}
          </div>
        </div>
        {/* Device toggle */}
        <div className="flex gap-1 p-1 rounded-xl bg-slate-100 shrink-0">
          {[{ id: "mobile", label: "جوال", icon: Smartphone }, { id: "desktop", label: "كمبيوتر", icon: Monitor }].map(({ id, label, icon: Icon }) => (
            <button key={id} onClick={() => onToggle(id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${viewMode === id ? "bg-[var(--bg-card)] text-[var(--text-primary)] shadow-sm" : "text-[var(--text-secondary)] hover:text-[var(--text-secondary)]"}`}>
              <Icon className="w-3 h-3" /> {label}
            </button>
          ))}
        </div>
      </div>
      {/* Score bar */}
      <div className="h-1.5 bg-slate-100">
        <div className="h-full rounded-full transition-all duration-1000"
          style={{ width: `${overallScore}%`, background: `linear-gradient(90deg, ${col}88, ${col})` }} />
      </div>
    </div>
  );
}

/* ── Priority Action Plan ────────────────────────────────────────────── */
function PriorityActions({ opportunities, navigate }) {
  if (!opportunities || opportunities.length === 0) return null;
  const groups = {
    high:   opportunities.filter(o => parseFloat(o.displayValue) >= 2  || parseFloat(o.score || 0) < 0.5),
    medium: opportunities.filter(o => parseFloat(o.displayValue) >= 0.5 && parseFloat(o.displayValue) < 2),
    low:    opportunities.filter(o => parseFloat(o.displayValue) < 0.5),
  };
  const order = ["high", "medium", "low"];
  const labels = { high: "أعلى تأثير", medium: "تأثير متوسط", low: "إصلاح سريع" };
  const colors = {
    high:   { dot: "bg-red-500",    bg: "bg-red-50",    badge: "bg-red-50 text-red-600 border-red-100",    num: "bg-red-500"   },
    medium: { dot: "bg-amber-400",  bg: "bg-amber-50",  badge: "bg-amber-50 text-amber-700 border-amber-100", num: "bg-amber-400" },
    low:    { dot: "bg-emerald-400",bg: "bg-emerald-50",badge: "bg-emerald-50 text-emerald-700 border-emerald-100", num: "bg-emerald-400"},
  };
  const allSorted = [
    ...groups.high.map(o => ({ ...o, _prio: "high" })),
    ...groups.medium.map(o => ({ ...o, _prio: "medium" })),
    ...groups.low.map(o => ({ ...o, _prio: "low" })),
  ];
  return (
    <div className="bg-[var(--bg-card)] rounded-2xl border border-[var(--border-color)] overflow-hidden shadow-sm" data-testid="priority-actions">
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-50"
        style={{ background: "linear-gradient(to left, #FFFBEB, #fff)" }}>
        <div className="flex items-center gap-2">
          <Target className="w-4 h-4 text-amber-500" />
          <span className="text-sm font-bold text-[var(--text-primary)]">خطة الإصلاح ذات الأولوية</span>
        </div>
        <div className="flex gap-2">
          {order.map(k => groups[k].length > 0 && (
            <span key={k} className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${colors[k].badge}`}>
              {groups[k].length} {labels[k]}
            </span>
          ))}
        </div>
      </div>
      <div className="divide-y divide-slate-50">
        {allSorted.slice(0, 8).map((opp, i) => {
          const c = colors[opp._prio];
          return (
            <div key={i} className={`flex items-start gap-3 px-5 py-3 hover:${c.bg}/60 transition-colors`}>
              <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-white text-[10px] font-black shrink-0 mt-0.5 ${c.num}`}>{i + 1}</span>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold text-[var(--text-secondary)]">{opp.title}</div>
                {opp.displayValue && (
                  <div className="text-[11px] text-[var(--text-muted)] mt-0.5 font-mono">{opp.displayValue}</div>
                )}
              </div>
              <span className={`text-[10px] font-bold shrink-0 px-2 py-0.5 rounded-full border ${c.badge}`}>
                {labels[opp._prio]}
              </span>
            </div>
          );
        })}
      </div>
      {allSorted.length > 8 && (
        <div className="px-5 py-3 border-t border-slate-50 text-center">
          <span className="text-[11px] text-[var(--text-muted)]">+ {allSorted.length - 8} مشكلة إضافية</span>
        </div>
      )}
    </div>
  );
}

/* ── History Grid (empty state) ─────────────────────────────────────── */
function HistoryGrid({ items, onSelect }) {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <h3 className="text-[10px] font-bold text-[var(--text-muted)] uppercase tracking-wider mb-3">آخر التحليلات</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
        {items.slice(0, 6).map(h => {
          const perf = h.mobile?.scores?.performance || 0;
          const col  = scoreColor(perf);
          const bg   = scoreBg(perf);
          return (
            <button key={h.id} onClick={() => onSelect(h)}
              className="text-right flex items-center gap-3 p-3.5 rounded-xl border border-[var(--border-color)] bg-[var(--bg-card)] hover:border-[#004EEB]/30 hover:shadow-md transition-all group shadow-sm">
              <div className="w-12 h-12 rounded-xl grid place-items-center shrink-0" style={{ background: bg }}>
                <span className="text-lg font-black mono" style={{ color: col }}>{perf}</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold text-[var(--text-secondary)] truncate">{h.url?.replace(/^https?:\/\//, "")}</div>
                <div className="text-[10px] text-[var(--text-muted)] mt-0.5">{new Date(h.created_at).toLocaleDateString("ar-SA")}</div>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-[#004EEB] transition-colors shrink-0 rtl:rotate-180" />
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ── Image Optimizer ──────────────────────────────────────────────────── */
function ImageOptimizer() {
  const [dragging, setDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef();

  const processFile = async (f) => {
    if (!f || !f.type.startsWith("image/")) { toast.error("يُسمح بالصور فقط"); return; }
    setResult(null); setLoading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/optimizer/compress-image?quality=82", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success(`تم الضغط! وفّرت ${data.savings_percent}%`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل ضغط الصورة");
    } finally { setLoading(false); }
  };

  const onDrop = useCallback((e) => {
    e.preventDefault(); setDragging(false);
    const f = e.dataTransfer.files[0]; if (f) processFile(f);
  }, []);

  const download = () => {
    if (!result) return;
    const a = document.createElement("a");
    a.href = `data:image/webp;base64,${result.image_base64}`;
    a.download = result.filename; a.click();
  };

  return (
    <div data-testid="image-optimizer-section">
      {!result && (
        <div
          className="rounded-xl cursor-pointer transition-all duration-200 text-center py-10"
          style={{ border: `2px dashed ${dragging ? "#004EEB" : "var(--border-color)"}`, background: dragging ? "rgba(0,78,235,0.04)" : "var(--bg-card-hover)" }}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          onClick={() => fileRef.current?.click()}
        >
          <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid="image-upload-input"
            onChange={(e) => { const f = e.target.files[0]; if (f) processFile(f); e.target.value = ""; }} />
          {loading ? (
            <div className="flex flex-col items-center gap-3">
              <div className="w-10 h-10 border-2 border-[#004EEB] border-t-transparent rounded-full animate-spin" />
              <span className="text-sm text-[var(--text-secondary)]">جاري الضغط والتحويل...</span>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-3">
              <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center border border-blue-100">
                <FileImage className="w-7 h-7 text-[#004EEB]" />
              </div>
              <div>
                <div className="text-sm font-bold text-[var(--text-secondary)] mb-0.5">اسحب صورة هنا أو انقر للرفع</div>
                <div className="text-xs text-[var(--text-muted)]">PNG · JPG · JPEG · WebP — الحد الأقصى 10MB</div>
              </div>
              <button type="button" className="px-4 py-2 bg-[#004EEB] text-white text-xs font-bold rounded-lg hover:bg-[#003BBB] transition-colors">
                اختر صورة
              </button>
            </div>
          )}
        </div>
      )}
      {result && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2.5 bg-emerald-50 border-b border-emerald-100">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              <span className="text-xs font-bold text-emerald-700">تم الضغط — وفّرت {result.savings_percent}%</span>
            </div>
            <button onClick={() => setResult(null)} className="text-[var(--text-muted)] hover:text-[var(--text-secondary)]"><X className="w-4 h-4" /></button>
          </div>
          <div className="p-4">
            <div className="grid grid-cols-4 gap-3 mb-4">
              {[
                { label: "الحجم الأصلي", val: fmtBytes(result.original_size), color: "#EF4444", bg: "#FEF2F2" },
                { label: "الحجم الجديد", val: fmtBytes(result.optimized_size), color: "#10B981", bg: "#ECFDF5" },
                { label: "التوفير",       val: result.savings_percent + "%",   color: "#004EEB", bg: "#EFF6FF" },
                { label: "الأبعاد",       val: result.new_dimensions,           color: "#8B5CF6", bg: "#F5F3FF" },
              ].map((m) => (
                <div key={m.label} className="text-center rounded-lg p-2.5" style={{ background: m.bg }}>
                  <div className="text-base font-bold font-mono" style={{ color: m.color }}>{m.val}</div>
                  <div className="text-[9px] text-[var(--text-secondary)] mt-0.5">{m.label}</div>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <button onClick={download} data-testid="download-compressed-btn"
                className="flex items-center gap-1.5 px-4 py-2 bg-[#004EEB] text-white text-xs font-bold rounded-lg hover:bg-[#003BBB] transition-colors">
                <Download className="w-3.5 h-3.5" /> تحميل WebP
              </button>
              <button onClick={() => setResult(null)}
                className="flex items-center gap-1.5 px-4 py-2 border border-[var(--border-color)] text-[var(--text-secondary)] text-xs font-semibold rounded-lg hover:bg-[var(--bg-card-hover)] transition-colors">
                <RefreshCw className="w-3.5 h-3.5" /> صورة أخرى
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════
   MAIN PAGE — محرك النمو
═══════════════════════════════════════ */
export default function StoreOptimizer() {
  const { logo_base64, name: platformName } = usePlatform();
  const { user } = useContext(AuthContext);
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadStep, setLoadStep] = useState(0);
  const [analysis, setAnalysis] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [recs, setRecs] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [activeTab, setActiveTab] = useState("analysis");
  const [sallaConnected, setSallaConnected] = useState(false);
  const [viewMode, setViewMode] = useState("mobile");

  const STEPS = [
    "الاتصال بـ Google PageSpeed...",
    "تحليل أداء الجوال...",
    "تحليل سطح المكتب...",
    "معالجة النتائج..."
  ];

  // Re-fetch salla status whenever the logged-in user changes (e.g. after Salla install redirect)
  useEffect(() => {
    if (user) {
      api.get("/optimizer/actions").then(({ data }) => setSallaConnected(data.salla_connected)).catch(() => {});
    }
  }, [user]);

  const analyze = async () => {
    if (!url.trim()) { toast.error("أدخل رابط المتجر أولاً"); return; }
    setLoading(true); setAnalysis(null); setRecs(null); setLoadStep(0);
    const timer = setInterval(() => setLoadStep((p) => Math.min(p + 1, STEPS.length - 1)), 5000);
    try {
      const { data: job } = await api.post("/optimizer/analyze", { url: url.trim() });
      let attempts = 0;
      while (attempts < 60) {
        await new Promise((r) => setTimeout(r, 2000));
        attempts++;
        const { data } = await api.get(`/optimizer/analyze/${job.id}`);
        if (data.status === "ready") {
          setAnalysis(data);
          toast.success("اكتمل التحليل!");
          generateRecs(data.id);
          return;
        } else if (data.status === "failed") {
          toast.error(data.error || "فشل التحليل");
          return;
        }
      }
      toast.error("انتهت مهلة الانتظار");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل التحليل");
    } finally { clearInterval(timer); setLoading(false); }
  };

  const generateRecs = async (id) => {
    setAiLoading(true); setRecs(null);
    try {
      const { data } = await api.post("/optimizer/ai-recommend", { analysis_id: id });
      setRecs(data.recommendations);
    } catch { toast.error("فشل توليد التوصيات"); }
    finally { setAiLoading(false); }
  };

  const loadHistory = async (silent = false) => {
    try {
      const { data } = await api.get("/optimizer/history");
      setHistory(data);
      if (!silent) setShowHistory(true);
      // Auto-fill URL from most recent analysis
      if (data.length > 0 && !url) {
        setUrl(data[0].url.replace(/^https?:\/\//, ""));
      }
    } catch {}
  };

  // Load history silently on mount to pre-fill URL & show recent analyses
  useEffect(() => { loadHistory(true); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const mob = analysis?.mobile;
  const desk = analysis?.desktop;
  const activeData = viewMode === "mobile" ? mob : desk;

  // Derived: overall score (avg of 4 metrics)
  const overallScore = activeData?.scores
    ? Math.round(([
        activeData.scores.performance,
        activeData.scores.seo,
        activeData.scores.accessibility,
        activeData.scores.bestPractices,
      ].reduce((a, b) => a + (b || 0), 0)) / 4)
    : null;
  const scoreGrade = overallScore == null ? "—"
    : overallScore >= 90 ? "A"
    : overallScore >= 75 ? "B"
    : overallScore >= 60 ? "C"
    : overallScore >= 45 ? "D"
    : "F";
  // Compare to last analysis in history
  const lastScore = history[0]?.mobile?.scores?.performance || null;
  const scoreDiff = analysis && lastScore && (mob?.scores?.performance - lastScore);
  // Quick wins = opportunities with low displayValue savings
  const quickWins = (activeData?.opportunities || [])
    .filter(o => parseFloat(o.displayValue) < 1)
    .slice(0, 3);

  const TABS = [
    { id: "analysis", label: "تحليل المتجر",   sub: "اكتشف ما يبطّئك",       icon: BarChart2 },
    { id: "auto",     label: "تحسين تلقائي",   sub: "جسر يحسّن عنك",         icon: Rocket    },
    { id: "seo",      label: "SEO ذكي",         sub: "تحسين البحث والمبيعات", icon: Search    },
    { id: "scripts",  label: "سكربتات سلة",    sub: "انسخ والصق مرة واحدة",  icon: Code2     },
    { id: "manual",   label: "خطوات يدوية",    sub: "تفعلها أنت في سلة",     icon: Wrench    },
  ];

  return (
    <div dir="rtl" style={{ fontFamily: "'Tajawal', sans-serif", background: "var(--bg-page)", minHeight: "100vh" }}>

      {/* ══ HERO HEADER ══ */}
      <div className="relative overflow-hidden" style={{
        background: "linear-gradient(135deg, #0F172A 0%, #1E3A8A 60%, #004EEB 100%)",
      }}>
        {/* Noise texture overlay */}
        <div className="absolute inset-0 opacity-[0.03]"
          style={{ backgroundImage: "url('https://static.prod-images.emergentagent.com/jobs/996d8047-3e1a-4e6b-acbb-a7b1ca0a6511/images/8b36c9015cf8a31918859ad0c696da26764b90170e67cdafc992db6a49a118d0.png')", backgroundSize: "200px" }} />
        {/* Grid pattern */}
        <div className="absolute inset-0 opacity-[0.06]"
          style={{ backgroundImage: "linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)", backgroundSize: "40px 40px" }} />

        <div className="relative max-w-6xl mx-auto px-6 py-5">
          <div className="flex items-center justify-between gap-6">
            <div className="flex-1">
              {/* Eyebrow */}
              <div className="flex items-center gap-2 mb-2">
                <div className="flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold tracking-widest uppercase"
                  style={{ background: "rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.7)", border: "1px solid rgba(255,255,255,0.15)" }}>
                  <Zap className="w-3 h-3" />
                  AI-Powered
                </div>
                <div className="h-px flex-1 max-w-16" style={{ background: "rgba(255,255,255,0.1)" }} />
              </div>

              {/* Main Title */}
              <h1 className="text-3xl sm:text-4xl font-black text-white mb-2 leading-tight">
                محرك النمو
              </h1>
              <p className="text-sm text-blue-200 max-w-xl leading-relaxed mb-4" style={{ opacity: 0.85 }}>
                حلّل أداء متجرك، اكتشف الفرص الخفية، وطبّق التحسينات تلقائياً — كل ذلك في مكان واحد
              </p>

              {/* Stat Pills */}
              <div className="flex flex-wrap gap-2">
                {[
                  { icon: Target, label: "تحليل PageSpeed كامل" },
                  { icon: Sparkles, label: "توصيات بالذكاء الاصطناعي" },
                  { icon: ArrowUpRight, label: "تحسين تلقائي للصور" },
                ].map((s) => {
                  const Icon = s.icon;
                  return (
                    <div key={s.label} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold"
                      style={{ background: "rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.8)", border: "1px solid rgba(255,255,255,0.12)" }}>
                      <Icon className="w-3 h-3" />
                      {s.label}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* ── HERO LOGO — Premium Orbital ── */}
            <div className="hidden lg:flex items-center justify-center relative shrink-0 me-4" style={{ width: 200, height: 200 }}>

              {/* Ambient glow blob — large soft */}
              <div className="absolute inset-0 rounded-full pointer-events-none"
                style={{ background: "radial-gradient(circle, rgba(96,165,250,0.22) 0%, rgba(0,78,235,0.10) 40%, transparent 70%)" }} />

              {/* Outer ring — slow dashed */}
              <svg className="absolute inset-0 w-full h-full" viewBox="0 0 300 300"
                style={{ animation: "hero-spin 22s linear infinite" }}>
                <circle cx="150" cy="150" r="138" fill="none"
                  stroke="rgba(255,255,255,0.12)" strokeWidth="1"
                  strokeDasharray="5 14" strokeLinecap="round" />
                {/* Dot nodes at 4 corners */}
                <circle cx="150" cy="12"  r="3" fill="rgba(96,165,250,0.7)" />
                <circle cx="288" cy="150" r="3" fill="rgba(96,165,250,0.7)" />
                <circle cx="150" cy="288" r="3" fill="rgba(96,165,250,0.7)" />
                <circle cx="12"  cy="150" r="3" fill="rgba(96,165,250,0.7)" />
              </svg>

              {/* Middle ring — dotted, reverse */}
              <svg className="absolute inset-0 w-full h-full" viewBox="0 0 300 300"
                style={{ animation: "hero-spin 14s linear infinite reverse" }}>
                <circle cx="150" cy="150" r="108" fill="none"
                  stroke="rgba(147,197,253,0.18)" strokeWidth="1.5"
                  strokeDasharray="2 9" strokeLinecap="round" />
                {/* Bright arc segment */}
                <path d="M 150 42 A 108 108 0 0 1 258 150" fill="none"
                  stroke="rgba(96,165,250,0.55)" strokeWidth="2" strokeLinecap="round" />
              </svg>

              {/* Inner ring — fast glowing arc */}
              <svg className="absolute inset-0 w-full h-full" viewBox="0 0 300 300"
                style={{ animation: "hero-spin 7s linear infinite" }}>
                <circle cx="150" cy="150" r="78" fill="none"
                  stroke="rgba(255,255,255,0.07)" strokeWidth="1"
                  strokeDasharray="4 20" />
                {/* Fast bright comet arc */}
                <path d="M 150 72 A 78 78 0 0 1 228 150" fill="none"
                  stroke="rgba(96,165,250,0.75)" strokeWidth="2.5" strokeLinecap="round" />
                {/* Comet head */}
                <circle cx="228" cy="150" r="5" fill="#60A5FA"
                  style={{ filter: "drop-shadow(0 0 8px #60A5FA)" }} />
              </svg>

              {/* Center logo — no frame, no text */}
              <div className="relative z-10 flex items-center justify-center">
                {/* Subtle radial glow behind logo */}
                <div className="absolute rounded-full pointer-events-none"
                  style={{ width: 100, height: 100, background: "radial-gradient(circle, rgba(0,78,235,0.45) 0%, transparent 70%)", filter: "blur(14px)" }} />
                {logo_base64
                  ? <img src={logo_base64} alt={platformName}
                      className="relative"
                      style={{ width: 80, height: 80, objectFit: "contain", filter: "drop-shadow(0 0 22px rgba(96,165,250,0.55))" }} />
                  : <JasrMark size={80} className="relative" />
                }
              </div>

              {/* Score badge — bottom left */}
              {analysis && (
                <div className="absolute bottom-4 start-4 flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-black shadow-lg z-20"
                  style={{ background: scoreColor(mob?.scores?.performance || 0), color: "white", boxShadow: `0 4px 14px ${scoreColor(mob?.scores?.performance || 0)}66` }}>
                  <Activity className="w-3 h-3" />
                  {mob?.scores?.performance || 0}
                </div>
              )}
            </div>
          </div>

          {/* Tab Navigation inside hero */}
          <div className="mt-4 flex" style={{ borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
            {TABS.map((tab) => {
              const Icon = tab.icon;
              const active = activeTab === tab.id;
              return (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)} data-testid={`tab-${tab.id}`}
                  className="flex items-center gap-2 px-4 py-3 text-right transition-all relative flex-1 justify-center sm:justify-start sm:flex-none"
                  style={{
                    color: active ? "#fff" : "rgba(255,255,255,0.45)",
                    borderBottom: active ? "2px solid #60A5FA" : "2px solid transparent",
                    marginBottom: "-1px",
                    background: active ? "rgba(255,255,255,0.06)" : "transparent",
                  }}>
                  <div className="w-6 h-6 rounded-lg flex items-center justify-center shrink-0"
                    style={{ background: active ? "rgba(96,165,250,0.2)" : "transparent" }}>
                    <Icon className="w-3.5 h-3.5" />
                  </div>
                  <div className="hidden sm:block text-right">
                    <div className="text-xs font-bold leading-tight whitespace-nowrap">{tab.label}</div>
                    <div className="text-[9px] mt-0.5 leading-none whitespace-nowrap hidden md:block"
                      style={{ color: active ? "rgba(147,197,253,0.8)" : "rgba(255,255,255,0.3)" }}>{tab.sub}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* ══ CONTENT ══ */}
      <div className="max-w-6xl mx-auto px-6 py-8">

        {/* ── TAB 1: Analysis ── */}
        {activeTab === "analysis" && (
          <div className="space-y-6">

            {/* URL Input */}
            <div className="bg-[var(--bg-card)] rounded-2xl border border-[var(--border-color)] p-5 shadow-sm">
              <div className="flex items-center gap-2 mb-3">
                <Globe className="w-4 h-4 text-[#004EEB]" />
                <span className="text-sm font-bold text-[var(--text-secondary)]">أدخل رابط متجرك لبدء التحليل</span>
                {history.length === 0 && (
                  <button onClick={loadHistory} data-testid="history-btn" className="ms-auto flex items-center gap-1.5 text-xs text-[var(--text-muted)] hover:text-[#004EEB] transition-colors">
                    <Clock className="w-3.5 h-3.5" /> التحليلات السابقة
                  </button>
                )}
                {history.length > 0 && (
                  <button onClick={() => setShowHistory(!showHistory)} data-testid="history-btn"
                    className="ms-auto flex items-center gap-1.5 text-xs text-[#004EEB] font-bold">
                    <Clock className="w-3.5 h-3.5" /> {history.length} تحليل سابق
                  </button>
                )}
              </div>
              <div className="flex gap-3">
                <div className="flex-1 flex items-center gap-2 rounded-xl px-4 py-3 border border-[var(--border-color)] focus-within:border-[#004EEB] focus-within:ring-2 focus-within:ring-[#004EEB]/10 transition-all bg-[var(--bg-card-hover)]">
                  <span className="text-[var(--text-muted)] text-xs shrink-0">https://</span>
                  <input type="text" value={url} onChange={(e) => setUrl(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && !loading && analyze()}
                    placeholder="mystore.salla.sa" data-testid="store-url-input"
                    className="flex-1 bg-transparent text-[var(--text-primary)] placeholder-slate-400 outline-none text-sm" dir="ltr" />
                </div>
                <button onClick={analyze} disabled={loading} data-testid="analyze-btn"
                  className="flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-bold text-white transition-all shrink-0 hover:shadow-lg hover:shadow-blue-200 disabled:opacity-60 disabled:cursor-not-allowed"
                  style={{ background: loading ? "#6B7280" : "#004EEB" }}>
                  {loading ? <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : <Search className="w-4 h-4" />}
                  {loading ? "جاري التحليل..." : "تحليل المتجر"}
                </button>
              </div>
            </div>

            {/* History Panel */}
            {showHistory && history.length > 0 && (
              <div className="bg-[var(--bg-card)] rounded-2xl border border-[var(--border-color)] p-4 shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-bold text-[var(--text-secondary)]">التحليلات السابقة ({history.length})</span>
                  <button onClick={() => setShowHistory(false)} className="text-[var(--text-muted)] hover:text-[var(--text-secondary)]"><X className="w-4 h-4" /></button>
                </div>
                <div className="space-y-1">
                  {history.slice(0, 8).map((h) => (
                    <button key={h.id} onClick={() => { setAnalysis(h); setRecs(h.ai_recommendations || null); setShowHistory(false); }}
                      data-testid={`history-item-${h.id}`}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-right hover:bg-[var(--bg-card-hover)] border border-transparent hover:border-[var(--border-color)] transition-all">
                      <Globe className="w-3.5 h-3.5 text-[var(--text-muted)] shrink-0" />
                      <span className="flex-1 text-xs text-[var(--text-secondary)] truncate">{h.url}</span>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="text-xs font-bold" style={{ color: scoreColor(h.mobile?.scores?.performance || 0) }}>
                          {h.mobile?.scores?.performance || 0}
                        </span>
                        <span className="text-[9px] text-[var(--text-muted)]">{new Date(h.created_at).toLocaleDateString("ar-SA")}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Loading */}
            {loading && (
              <div className="bg-[var(--bg-card)] rounded-2xl border border-[var(--border-color)] p-10 text-center shadow-sm">
                <div className="relative w-16 h-16 mx-auto mb-6">
                  <div className="absolute inset-0 rounded-full border-4 border-[#004EEB]/10" />
                  <div className="absolute inset-0 rounded-full border-4 border-[#004EEB] border-t-transparent animate-spin" />
                  <div className="absolute inset-2 rounded-full flex items-center justify-center bg-[#004EEB]/5">
                    <Zap className="w-5 h-5 text-[#004EEB]" />
                  </div>
                </div>
                <div className="text-sm font-bold text-[var(--text-secondary)] mb-1">{STEPS[loadStep]}</div>
                <div className="text-xs text-[var(--text-muted)] mb-5">التحليل الكامل قد يستغرق 30-60 ثانية</div>
                <div className="flex justify-center gap-2">
                  {STEPS.map((_, i) => (
                    <div key={i} className="w-2 h-2 rounded-full transition-all duration-300"
                      style={{ background: i <= loadStep ? "#004EEB" : "#E2E8F0", transform: i === loadStep ? "scale(1.3)" : "scale(1)" }} />
                  ))}
                </div>
              </div>
            )}

            {/* Results */}
            {analysis && !loading && (
              <div className="space-y-5">

                {/* Estimated Banner */}
                {analysis.is_estimated && (
                  <div className="rounded-2xl p-4 flex items-start gap-3"
                    style={{ background: "#EFF6FF", border: "1px solid #BFDBFE" }}
                    data-testid="estimated-banner">
                    <Info className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
                    <div>
                      <div className="text-xs font-bold text-blue-800 mb-0.5">تحليل تقريبي</div>
                      <p className="text-xs text-blue-700">
                        بناءً على قياسات HTTP مباشرة (PageSpeed API واجه تأخيراً). النتائج تقريبية.{" "}
                        <a href="https://console.developers.google.com/apis/library/pagespeedonline.googleapis.com"
                          target="_blank" rel="noreferrer" className="underline font-medium">تحقق من PageSpeed API</a>
                      </p>
                    </div>
                  </div>
                )}

                {/* Score Cards */}
                <div className="bg-[var(--bg-card)] rounded-2xl border border-[var(--border-color)] overflow-hidden shadow-sm" data-testid="scores-section">
                  <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border-color)]">
                    <div className="flex items-center gap-2">
                      <Activity className="w-4 h-4 text-[#004EEB]" />
                      <span className="text-sm font-bold text-[var(--text-primary)]">نتائج الأداء</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {/* Mobile/Desktop Toggle */}
                      <div className="flex gap-1 p-1 rounded-lg bg-slate-100">
                        {[
                          { id: "mobile", label: "جوال", icon: Smartphone },
                          { id: "desktop", label: "سطح المكتب", icon: Monitor },
                        ].map(({ id, label, icon: Icon }) => (
                          <button key={id} onClick={() => setViewMode(id)}
                            className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-bold transition-all ${viewMode === id ? "bg-[var(--bg-card)] text-[var(--text-primary)] shadow-sm" : "text-[var(--text-secondary)] hover:text-[var(--text-secondary)]"}`}>
                            <Icon className="w-3 h-3" /> {label}
                          </button>
                        ))}
                      </div>
                      <span className="text-xs text-[var(--text-muted)] truncate max-w-40 hidden sm:block">{analysis.url}</span>
                    </div>
                  </div>

                  <div className="p-5">
                    {/* Main scores grid */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                      <ScoreGauge score={activeData?.scores?.performance || 0} label="الأداء" size={120} />
                      <ScoreGauge score={activeData?.scores?.seo || 0} label="SEO" size={120} />
                      <ScoreGauge score={activeData?.scores?.accessibility || 0} label="إمكانية الوصول" size={120} />
                      <ScoreGauge score={activeData?.scores?.bestPractices || 0} label="أفضل الممارسات" size={120} />
                    </div>

                    {/* Core Web Vitals */}
                    <div className="border-t border-[var(--border-color)] pt-4">
                      <div className="flex items-center gap-1.5 text-xs font-bold text-[var(--text-secondary)] mb-3">
                        <BarChart2 className="w-3.5 h-3.5" /> Core Web Vitals
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2" data-testid="vitals-grid">
                        <VitalCard label="LCP" data={activeData?.vitals?.lcp} />
                        <VitalCard label="FCP" data={activeData?.vitals?.fcp} />
                        <VitalCard label="TBT" data={activeData?.vitals?.tbt} />
                        <VitalCard label="CLS" data={activeData?.vitals?.cls} />
                        <VitalCard label="TTI" data={activeData?.vitals?.tti} />
                        <VitalCard label="Speed Index" data={activeData?.vitals?.speedIndex} />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Opportunities */}
                {activeData?.opportunities?.length > 0 && (
                  <div className="bg-[var(--bg-card)] rounded-2xl border overflow-hidden shadow-sm"
                    style={{ borderColor: "#FDE68A" }}
                    data-testid="opportunities-section">
                    <div className="flex items-center gap-2 px-5 py-3.5 border-b"
                      style={{ background: "#FFFBEB", borderColor: "#FDE68A" }}>
                      <AlertTriangle className="w-4 h-4 text-amber-500" />
                      <span className="text-sm font-bold text-amber-800">المشاكل المكتشفة</span>
                      <span className="ms-auto text-xs font-bold px-2 py-0.5 rounded-full"
                        style={{ background: "#FEF3C7", color: "#92400E" }}>{activeData.opportunities.length} مشكلة</span>
                    </div>
                    <div className="divide-y divide-slate-50">
                      {activeData.opportunities.map((opp, i) => (
                        <div key={i} className="flex items-center gap-3 px-5 py-2.5 hover:bg-amber-50/30 transition-colors">
                          <AlertTriangle className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                          <span className="flex-1 text-xs text-[var(--text-secondary)]">{opp.title}</span>
                          {opp.displayValue && <span className="text-xs font-mono font-bold text-amber-600 shrink-0">{opp.displayValue}</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* AI Recommendations */}
                <div className="bg-[var(--bg-card)] rounded-2xl border border-violet-100 overflow-hidden shadow-sm"
                  data-testid="recommendations-section">
                  <div className="flex items-center gap-2 px-5 py-4 border-b border-violet-100"
                    style={{ background: "linear-gradient(to left, #F5F3FF, #fff)" }}>
                    <Sparkles className="w-4 h-4 text-violet-500" />
                    <span className="text-sm font-bold text-violet-800">توصيات الذكاء الاصطناعي</span>
                    {aiLoading && (
                      <div className="ms-auto flex items-center gap-1.5 text-xs text-violet-500">
                        <div className="w-3 h-3 border border-violet-400 border-t-transparent rounded-full animate-spin" />
                        جاري التحليل بالذكاء الاصطناعي...
                      </div>
                    )}
                    {recs && !aiLoading && (
                      <button onClick={() => generateRecs(analysis.id)}
                        className="ms-auto flex items-center gap-1 text-xs text-violet-500 hover:text-violet-700 transition-colors">
                        <RefreshCw className="w-3 h-3" /> إعادة التوليد
                      </button>
                    )}
                  </div>
                  <div className="p-4">
                    {aiLoading && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {[1,2,3,4].map((i) => (
                          <div key={i} className="rounded-xl p-4 border border-[var(--border-color)] animate-pulse">
                            <div className="flex gap-3">
                              <div className="w-9 h-9 rounded-xl bg-slate-100" />
                              <div className="flex-1 space-y-2 pt-1">
                                <div className="h-3 bg-slate-100 rounded w-1/2" />
                                <div className="h-2 bg-slate-100 rounded w-3/4" />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {recs && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {recs.map((r, i) => <RecommendationCard key={i} rec={r} idx={i} />)}
                      </div>
                    )}
                    {!recs && !aiLoading && (
                      <div className="text-center py-6 text-sm text-[var(--text-muted)]">جاري توليد التوصيات...</div>
                    )}
                  </div>
                </div>

                {/* Optimization Loop */}
                <OptimizationLoop analysis={analysis} />

              </div>
            )}

            {/* Empty state */}
            {!analysis && !loading && (
              <div className="bg-[var(--bg-card)] rounded-2xl border border-dashed border-[var(--border-color)] p-14 text-center">
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
                  style={{ background: "#EFF6FF", border: "1px solid #BFDBFE" }}>
                  <Target className="w-8 h-8 text-[#004EEB]/50" />
                </div>
                <div className="text-sm font-bold text-[var(--text-secondary)] mb-2">أدخل رابط متجرك للبدء</div>
                <div className="text-xs text-[var(--text-muted)] max-w-sm mx-auto leading-relaxed">
                  سنحلل أداءك الكامل — سرعة التحميل، SEO، تجربة المستخدم — ونولّد توصيات ذكية مخصصة لمتجر سلة
                </div>
              </div>
            )}

            {/* Image Optimizer */}
            <div className="bg-[var(--bg-card)] rounded-2xl border border-[var(--border-color)] overflow-hidden shadow-sm">
              <div className="flex items-center justify-between px-5 py-3.5 border-b border-[var(--border-color)]" style={{ background: "#F8FAFC" }}>
                <div className="flex items-center gap-2">
                  <Image className="w-4 h-4 text-[#004EEB]" />
                  <span className="text-sm font-bold text-[var(--text-primary)]">ضغط الصور — تحويل فوري لـ WebP</span>
                </div>
                <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: "#EFF6FF", color: "#004EEB", border: "1px solid #BFDBFE" }}>
                  توفير حتى 70%
                </span>
              </div>
              <div className="p-4">
                <ImageOptimizer />
              </div>
            </div>
          </div>
        )}

        {/* ── TAB 2: Auto Optimization ── */}
        {activeTab === "auto" && (
          <div>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-9 h-9 rounded-xl border flex items-center justify-center shrink-0"
                style={{ background: "#ECFDF5", borderColor: "#A7F3D0" }}>
                <Rocket className="w-4 h-4 text-emerald-600" />
              </div>
              <div>
                <h3 className="text-sm font-black text-[var(--text-primary)]">تحسين تلقائي بالكامل</h3>
                <p className="text-xs text-[var(--text-muted)] mt-0.5">جسر يتصل بـ Salla ويُحسّن صور منتجاتك دون أي جهد منك</p>
              </div>
              <span className="ms-auto text-[9px] font-bold px-2.5 py-1 rounded-full border"
                style={{ background: "#EFF6FF", color: "#004EEB", borderColor: "#BFDBFE" }}>مُوصى به</span>
            </div>
            <BulkImageOptimizer sallaConnected={sallaConnected} />
          </div>
        )}

        {/* ── TAB 3: SEO ── */}
        {activeTab === "seo" && (
          <div className="px-4 sm:px-8 pb-8 pt-4">
            <SEOEngine sallaConnected={sallaConnected} />
          </div>
        )}

        {/* ── TAB 4: Scripts ── */}
        {activeTab === "scripts" && (
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-9 h-9 rounded-xl border flex items-center justify-center shrink-0"
                style={{ background: "#EFF6FF", borderColor: "#BFDBFE" }}>
                <Code2 className="w-4 h-4 text-[#004EEB]" />
              </div>
              <div>
                <h3 className="text-sm font-black text-[var(--text-primary)]">سكربتات الأداء الجاهزة</h3>
                <p className="text-xs text-[var(--text-muted)] mt-0.5">انسخ الكود مرة واحدة في لوحة سلة — تأثير فوري على سرعة المتجر</p>
              </div>
            </div>
            {/* How-to callout */}
            <div className="flex items-start gap-2.5 p-3.5 rounded-xl mb-5 mt-3"
              style={{ background: "#EFF6FF", border: "1px solid #BFDBFE" }}>
              <Info className="w-4 h-4 text-[#004EEB] shrink-0 mt-0.5" />
              <p className="text-xs text-blue-700 leading-relaxed">
                <span className="font-bold">كيف تُفعّل؟</span> افتح لوحة تحكم سلة ← التطبيقات ← الكود المخصص ← JavaScript ← الصق الكود ← احفظ. بس!
              </p>
            </div>
            <div className="space-y-3">
              <ScriptPanel type="all"  title="حزمة الأداء الكاملة — الأفضل للبداية" description="Lazy Loading + Resource Hints + تحسين الخطوط + Passive Listeners (كل شيء في سكربت واحد)" />
              <ScriptPanel type="lazy" title="تحميل كسول للصور" description="يُضيف loading=lazy للصور تلقائياً — يقلل وقت التحميل ويحسّن LCP" />
              <ScriptPanel type="hints" title="تسريع الاتصال بالموارد" description="DNS Prefetch + Preconnect للخطوط وسيرفرات سلة — تقليل latency" />
            </div>
          </div>
        )}

        {/* ── TAB 4: Manual Actions ── */}
        {activeTab === "manual" && (
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-9 h-9 rounded-xl bg-slate-100 border border-[var(--border-color)] flex items-center justify-center shrink-0">
                <Wrench className="w-4 h-4 text-[var(--text-secondary)]" />
              </div>
              <div>
                <h3 className="text-sm font-black text-[var(--text-primary)]">خطوات تفعلها أنت</h3>
                <p className="text-xs text-[var(--text-muted)] mt-0.5">تحسينات يدوية في لوحة سلة — كل خطوة واضحة ومشروحة</p>
              </div>
            </div>
            {/* Priority note */}
            <div className="flex items-start gap-2.5 p-3.5 rounded-xl mb-5 mt-3"
              style={{ background: "#FEF3C7", border: "1px solid #FDE68A" }}>
              <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
              <p className="text-xs text-amber-700 leading-relaxed">
                <span className="font-bold">ابدأ بالتأثير العالي أولاً.</span> حذف التطبيقات غير المستخدمة وتقليل المنتجات في الصفحة الرئيسية يمنحانك أكبر تحسين بأقل جهد.
              </p>
            </div>
            <div className="space-y-3">
              <ManualAction icon={Package} title="تقليل عدد المنتجات في الصفحة الرئيسية"
                description="كثرة المنتجات تزيد حجم الصفحة وتبطئ LCP — أهم خطوة فردية" impact="high"
                steps={["اذهب للوحة تحكم سلة > الثيم", "اختر تخصيص الثيم", "ابحث عن إعداد المنتجات في الصفحة الرئيسية", "قلل العدد إلى 8-12 منتج فقط", "احفظ التغييرات"]} />
              <ManualAction icon={Zap} title="حذف التطبيقات غير المستخدمة"
                description="كل تطبيق مثبت يضيف سكربتات تبطئ المتجر — احذف كل غير ضروري" impact="high"
                steps={["اذهب لسلة > التطبيقات > جميع التطبيقات", "راجع كل تطبيق مثبت", "احذف أي تطبيق لا تستخدمه بانتظام", "التأثير فوري — كل تطبيق محذوف يحسّن الأداء"]} />
              <ManualAction icon={Image} title="رفع صور المنتجات بالأبعاد المثالية"
                description="صور أكبر من 800×800 بكسل تُحمَّل بحجم أكبر دون فائدة مرئية" impact="medium"
                steps={["اضغط صورك قبل الرفع (استخدم أداة الضغط في تبويب التحليل)", "حدد أبعاد 800×800 بكسل لصور المنتجات", "استخدم WebP أو JPEG بجودة 80-85%", "ارفع الصور من لوحة تحكم المنتج في سلة"]} />
              <ManualAction icon={Code2} title="تحسين نصوص SEO للمنتجات الأساسية"
                description="العناوين والوصف يؤثران مباشرة على ترتيب المتجر في نتائج البحث" impact="medium"
                steps={["اذهب لسلة > المنتجات > رتّب حسب المبيعات", "افتح أعلى 20 منتجاً مبيعاً", "لكل منتج: أضف meta description واضح (150-160 حرف)", "تأكد من وجود كلمات مفتاحية في عنوان المنتج", "ارفع sitemap.xml لـ Google Search Console"]} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
